/*
  magnify.js - the click-to-zoom on a product photograph.
  Dawn 12.0.0's file, changed 2026-09-27 at the shop owner's direction. Their words: the zoom should
  "NOT auto reset when the mouse pointer is moved outside the image (only when the mouse pointer clicks
  out side the image)", and while zoomed "a zoom slider appear at bottom of image, at 50% opacity so the
  image can still be seen under slider", dragged left and right to zoom out and in.
  Version zoom-v2 (2026-09-27): the first version plus the fixes from its review.
  Version zoom-v3 (2026-09-27): v2 plus the owner's second request of the same day: "instead of a cursor icon
  which appears as a "+" when image is zoomed in, change the cursor to be a "hand" cursor which when click+hold
  happens the hand turns into a "closed hand" icon, and the visitor can move the cursor to drag around the
  visible parts of the image", a close "X" at the top left, and "a way to determine and set the max zoom-in
  level for each product image and ensure the zoom slider does not allow zoom level to go past that zoom-in level".
  Version zoom-v4 (2026-09-27): v3 plus the owner's request of the same evening: "make the "X" close button on the
  zoom viewer the same opacity as the zoom-slider" (item 9).

  WHAT IS DIFFERENT FROM DAWN, and nothing else is:
    1. Moving the pointer off the photograph no longer ends the zoom. The picture stays where it was.
    2. The zoom ends on a mouse press anywhere OUTSIDE the photograph, on a tap outside it, on Escape, or on
       the close button (item 9).
       With a mouse it is the press (pointerdown), on purpose: a visitor who drags the slider and lets go
       outside the photograph produces a click on the page around it, and that must not end the zoom.
       With a finger or a pen it is a TAP: a touch outside that lifts within 10 px of where it started.
       A finger swipe that scrolls the page is not a tap, so it does not end the zoom.
    3. The zoomed picture moves only when it is DRAGGED: press and hold on it, move, and it moves with the
       pointer, stopping at its own edges so no empty space shows. While zoomed the pointer is an open hand;
       while the button is held it is a closed hand, wherever the pointer goes. From the press to the release
       the picture keeps hold of the pointer (pointer capture), so a drag that passes over, or ends on, the
       slider or the close button does nothing to them. Merely moving the pointer over the picture does not
       move it, and a plain click inside does nothing (v2's click-to-hold is gone: with nothing following
       the pointer there is nothing to hold). A finger or pen drags the picture the same way; a swipe that
       starts OUTSIDE the photograph still scrolls the page.
    4. While zoomed, a slider sits at the bottom of the photograph at half opacity. It runs from 1x
       (the photograph exactly as it sits on the page) to this photograph's own limit (item 8), starting at
       Dawn's own 2x, or at the limit if that is lower. It works by dragging, by clicking the track, by
       clicking the - and + at its ends (0.5x a click), and from the keyboard (arrow keys, Home, End, Page Up
       and Page Down). Its clickable strip reaches 8 px above and below the visible bar, invisibly, so it is
       easy to grab. A mouse click on the - or + counts only if the press also began on it.
       If the bottom of the photograph is below the bottom of the window, the slider rides at the bottom
       of the part that can be seen, so it can always be reached without scrolling.
    5. The zoomed picture starts from the size at which the photograph fills its box on the page, and is
       enlarged from there. So it never repeats inside the box, and 1x is exactly the page's own view,
       framed boxes included.
    6. The zoom also closes when another picture is chosen, by mouse or by keyboard, and when the window
       becomes narrower than 750 px, where Dawn lays its full-screen viewer's button over the photograph.
    7. The styles for the slider and the close button travel inside this file, so the change is one file and
       removing it is one file. They are added to the page once, only on pages that load this script.
    8. THE ZOOM LIMIT, for each photograph: the photograph's own width in pixels, divided by (the width it is
       drawn at 1x on the page x the screen's pixel density), times ZOOM_TOLERANCE; never above 4, never
       below MIN_ZOOM_CAP; rounded DOWN to the slider's 0.1 step, so the slider reaches it exactly and never
       passes it. At the limit each screen pixel still has one pixel of the photograph behind it. It is
       worked out again when the window is resized and when the pixel density changes (browser zoom, or the
       window moved to another screen), and checked again whenever the visitor presses on the picture or uses
       the slider, in case a density change went unannounced. The two settings are the first lines of the code.
    9. A close button (an X in a round button, 44 x 44 px) sits at the top left of the zoomed photograph.
       A click on it closes the zoom exactly as a click outside does. It is reached by Tab and pressed with
       Enter or Space; its label is "Close zoom". A mouse click on it counts only if the press also began on
       it. If the top of the photograph is above the top of the window, it rides at the top of the part
       that can be seen, below the shop's header when that is pinned over the page.
       Its opacity follows the slider's exactly (item 4): half opacity, fully solid while it has keyboard
       focus, and fully solid in the high-contrast (forced colours) mode.

  WHAT IS THE SAME: the first click zooms to 2x (or the limit, if lower) on the point clicked, and the
  photograph dims with a spinner while the large copy loads. The large copy is still fetched only on that click.
  Phones and tablets never start this zoom: there, Dawn lays its full-screen viewer's button over the
  photograph (section-main-product.css, "@media (hover: hover)" and "max-width: 749px"), so a tap opens
  that viewer exactly as before. A touch screen whose main pointer is a mouse gets this zoom, as it did
  under Dawn; there a tap outside the photograph now closes it, where under Dawn only a tap on it did.

  RESTORING: put back zoom v2 (md5 b30b8b0394d1f0239294297b2eef1334), or Dawn's own copy
  (md5 63a0b729648db05c21789f0753a8f51d).
*/
(function () {
  'use strict';

  // The two settings of the zoom limit (item 8 above).
  // ZOOM_TOLERANCE: 1.0 stops exactly where the photograph's own pixels run out on the visitor's screen.
  //   1.25 would allow a quarter more (slightly soft); 0.8 would stop a fifth short of it.
  var ZOOM_TOLERANCE = 1.0;
  // MIN_ZOOM_CAP: the lowest the limit may ever be, so that a small photograph still zooms a little.
  var MIN_ZOOM_CAP = 1.5;

  var VERSION = 'zoom-v4-2026-09-27';
  var DEFAULT_ZOOM = 2; // Dawn's own level for the first click
  var MIN_ZOOM = 1;
  var MAX_ZOOM = 4; // no photograph's limit is ever above this
  var ZOOM_STEP = 0.1;
  var SLIDER_GAP_PX = 12;
  var TAP_SLOP_PX = 10; // a touch that travels further than this is a swipe, not a tap
  var NARROW_QUERY = '(max-width: 749px)'; // Dawn's own breakpoint for its viewer button over the photo
  var STYLE_ID = 'ImageMagnifyZoomStyle';
  var INSTALLED_FLAG = '__imageMagnifyZoomInstalled';
  var VERSION_FLAG = '__imageMagnifyZoomVersion';
  var DRAGGING_CLASS = 'image-magnify-dragging';

  var CSS = [
    '.product__media > .image-magnify-full-size { cursor: grab; touch-action: none; -webkit-user-select: none; user-select: none; }',
    '.product__media > .image-magnify-full-size[data-dragging="true"] { cursor: grabbing; }',
    // While a drag is on, the closed hand shows wherever the pointer goes, and no text is selected on the way.
    'html.' + DRAGGING_CLASS + ', html.' + DRAGGING_CLASS + ' * { cursor: grabbing !important; -webkit-user-select: none !important; user-select: none !important; }',
    '.image-magnify-zoom {',
    '  position: absolute; z-index: 3; left: 50%; bottom: ' + SLIDER_GAP_PX + 'px;',
    '  transform: translateX(-50%); box-sizing: border-box;',
    '  display: flex; align-items: center; gap: 1rem;',
    '  width: min(30rem, calc(100% - 2.4rem)); margin: 0; padding: 0.4rem 1.4rem;',
    '  border-radius: 10rem; background-color: rgb(var(--color-background)); color: rgb(var(--color-foreground));',
    '  opacity: 0.5; cursor: default; -webkit-user-select: none; user-select: none;',
    '}',
    '.image-magnify-zoom:has(.image-magnify-zoom__input:focus-visible) { opacity: 1; }',
    '.image-magnify-zoom__sign { flex: 0 0 auto; padding: 0.4rem; font-size: 1.8rem; line-height: 1; cursor: pointer; }',
    // The input is 5.2rem tall but takes only 2.8rem of the bar: the extra is an invisible strip above and
    // below the visible bar that still grabs the slider.
    '.image-magnify-zoom__input {',
    '  -webkit-appearance: none; appearance: none; flex: 1 1 auto; min-width: 0; height: 5.2rem;',
    '  margin: -1.2rem 0; padding: 0; background: transparent; cursor: pointer;',
    '}',
    '.image-magnify-zoom__input::-webkit-slider-runnable-track {',
    '  height: 0.4rem; border-radius: 0.2rem;',
    '  background: linear-gradient(to right, rgb(var(--color-foreground)) var(--image-magnify-fill, 33%),',
    '    rgba(var(--color-foreground), 0.35) var(--image-magnify-fill, 33%));',
    '}',
    '.image-magnify-zoom__input::-webkit-slider-thumb {',
    '  -webkit-appearance: none; appearance: none; width: 1.8rem; height: 1.8rem; margin-top: -0.7rem;',
    '  border: 0; border-radius: 50%; background: rgb(var(--color-foreground));',
    '}',
    '.image-magnify-zoom__input::-moz-range-track { height: 0.4rem; border-radius: 0.2rem; background: rgba(var(--color-foreground), 0.35); }',
    '.image-magnify-zoom__input::-moz-range-progress { height: 0.4rem; border-radius: 0.2rem; background: rgb(var(--color-foreground)); }',
    '.image-magnify-zoom__input::-moz-range-thumb { width: 1.8rem; height: 1.8rem; border: 0; border-radius: 50%; background: rgb(var(--color-foreground)); }',
    '.image-magnify-close {',
    '  position: absolute; z-index: 3; top: ' + SLIDER_GAP_PX + 'px; left: ' + SLIDER_GAP_PX + 'px;',
    '  box-sizing: border-box; display: flex; align-items: center; justify-content: center;',
    '  width: 4.4rem; height: 4.4rem; min-width: 44px; min-height: 44px; margin: 0; padding: 0;',
    '  border: 0; border-radius: 50%; cursor: pointer; -webkit-user-select: none; user-select: none;',
    '  background-color: rgb(var(--color-background)); color: rgb(var(--color-foreground));',
    '  box-shadow: 0 0 0 0.1rem rgba(var(--color-foreground), 0.3), 0 0.2rem 0.8rem rgba(0, 0, 0, 0.45);',
    '  opacity: 0.5;',
    '}',
    '.image-magnify-close:focus-visible { opacity: 1; }',
    '.image-magnify-close svg { width: 1.8rem; height: 1.8rem; pointer-events: none; }',
    '@media (forced-colors: active) { .image-magnify-zoom { opacity: 1; border: 1px solid CanvasText; } .image-magnify-close { opacity: 1; border: 1px solid ButtonText; } }',
  ].join('\n');

  // The one zoom open on the page, or null.
  var session = null;
  var narrow = window.matchMedia ? window.matchMedia(NARROW_QUERY) : null;

  function clamp(value, low, high) {
    return Math.min(high, Math.max(low, value));
  }

  function isNarrow() {
    return !!(narrow && narrow.matches);
  }

  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CSS;
    (document.head || document.documentElement).appendChild(style);
  }

  // Dawn's own lookup: the spinner sits beside the photograph's wrapper, inside the modal opener.
  function spinnerOf(image) {
    var holder = image.parentElement && image.parentElement.parentElement;
    return holder ? holder.querySelector('.loading__spinner') : null;
  }

  function levelText(level) {
    return String(Math.round(level * 10) / 10) + ' times';
  }

  // The scale at which the photograph fills its box on the page (its object-fit), so that 1x is the
  // page's own view. "cover" fills the box and crops; "contain" fits inside it.
  function fitScale(s, box) {
    var sx = box.width / s.naturalWidth;
    var sy = box.height / s.naturalHeight;
    var fit = window.getComputedStyle(s.image).objectFit;
    return fit === 'contain' || fit === 'scale-down' ? Math.min(sx, sy) : Math.max(sx, sy);
  }

  // The width, in CSS pixels, at which the whole photograph is drawn at 1x.
  function baseWidth(s, box) {
    return s.naturalWidth && s.naturalHeight ? s.naturalWidth * fitScale(s, box) : box.width;
  }

  // Item 8: the highest zoom at which each screen pixel still has a pixel of the photograph behind it.
  function zoomLimit(s) {
    var box = s.overlay ? s.overlay.getBoundingClientRect() : null;
    if (!box || !box.width || !box.height || !s.naturalWidth) return s.max; // cannot be measured now: keep it
    s.ratio = window.devicePixelRatio || 1;
    var exact = (s.naturalWidth / (baseWidth(s, box) * s.ratio)) * ZOOM_TOLERANCE;
    s.limitExact = exact;
    var stepped = Math.floor(exact / ZOOM_STEP + 1e-6) * ZOOM_STEP;
    return Math.round(clamp(stepped, MIN_ZOOM_CAP, MAX_ZOOM) * 1000) / 1000;
  }

  function render(s) {
    if (!s.overlay) return;
    var box = s.overlay.getBoundingClientRect();
    if (!box.width || !box.height) return; // the photograph is not on screen
    s.overlay.style.backgroundSize = baseWidth(s, box) * s.level + 'px';
    // At 1x the picture is placed exactly as the photograph sits on the page, whatever its framing.
    s.overlay.style.backgroundPosition =
      s.level <= MIN_ZOOM ? window.getComputedStyle(s.image).objectPosition : s.x + '% ' + s.y + '%';
  }

  // The top of the part of the window a visitor can see: below the shop's header when it is pinned over the page.
  function visibleTop() {
    var header = document.querySelector('.section-header');
    if (!header) return 0;
    var position = window.getComputedStyle(header).position;
    if (position !== 'sticky' && position !== 'fixed') return 0;
    var rect = header.getBoundingClientRect();
    return rect.height && rect.bottom > 0 ? rect.bottom : 0;
  }

  function placeControls(s) {
    var box = s.box.getBoundingClientRect();
    if (s.slider) {
      var windowHeight = window.innerHeight || document.documentElement.clientHeight;
      var belowWindow = Math.max(0, box.bottom - windowHeight);
      var room = Math.max(0, box.height - s.slider.offsetHeight - 2 * SLIDER_GAP_PX);
      s.slider.style.bottom = SLIDER_GAP_PX + Math.min(belowWindow, room) + 'px';
    }
    if (s.close) {
      var aboveView = Math.max(0, visibleTop() - box.top);
      var roomTop = Math.max(0, box.height - s.close.offsetHeight - 2 * SLIDER_GAP_PX);
      s.close.style.top = SLIDER_GAP_PX + Math.min(aboveView, roomTop) + 'px';
    }
  }

  function pointAt(s, event) {
    var rect = (s.overlay || s.image).getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    s.x = clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100);
    s.y = clamp(((event.clientY - rect.top) / rect.height) * 100, 0, 100);
  }

  function setLevel(s, value) {
    var level = parseFloat(value);
    s.level = clamp(isNaN(level) ? DEFAULT_ZOOM : level, MIN_ZOOM, s.max);
    if (s.input) {
      s.input.setAttribute('aria-valuetext', levelText(s.level));
      s.slider.style.setProperty('--image-magnify-fill', ((s.level - MIN_ZOOM) / (s.max - MIN_ZOOM)) * 100 + '%');
    }
    render(s);
  }

  // Works the limit out again (window resized, pixel density changed) and keeps the level inside it.
  function applyLimit(s) {
    s.max = zoomLimit(s);
    if (s.input) {
      s.input.max = String(s.max);
      s.input.setAttribute('data-zoom-limit-exact', String(s.limitExact));
    }
    setLevel(s, s.level);
    if (s.input) s.input.value = String(s.level);
  }

  // A change of pixel density does not always announce itself (measured 2026-09-27: under Chrome's own emulation
  // of one, neither the resolution query nor a resize fires), so the limit is also checked whenever the visitor
  // next presses on the picture or uses the slider.
  function checkRatio(s) {
    if (s.overlay && s.ratio !== (window.devicePixelRatio || 1)) applyLimit(s);
  }

  // A mouse click counts only when the press also began on the same control. A click from the keyboard
  // (Enter or Space on a button) has no press, and carries detail 0.
  function pressedHere(s, control, event) {
    return event.detail === 0 || !!(s.pressed && control.contains(s.pressed));
  }

  // The - and + at the ends of the slider. They are for the mouse; the keyboard already has the arrow keys,
  // so they stay out of the Tab order and hidden from screen readers.
  function nudge(s, delta) {
    checkRatio(s);
    setLevel(s, Math.round((s.level + delta) * 10) / 10);
    if (s.input) s.input.value = String(s.level);
  }

  function buildSlider(s) {
    var slider = document.createElement('div');
    slider.className = 'image-magnify-zoom';

    var minus = document.createElement('span');
    minus.className = 'image-magnify-zoom__sign';
    minus.setAttribute('aria-hidden', 'true');
    minus.textContent = '\u2212'; // the minus sign

    var input = document.createElement('input');
    input.type = 'range';
    input.className = 'image-magnify-zoom__input';
    input.min = String(MIN_ZOOM);
    input.max = String(s.max);
    input.step = String(ZOOM_STEP);
    input.value = String(s.level);
    input.setAttribute('aria-label', 'Zoom level');
    input.setAttribute('data-zoom-limit-exact', String(s.limitExact));

    var plus = document.createElement('span');
    plus.className = 'image-magnify-zoom__sign';
    plus.setAttribute('aria-hidden', 'true');
    plus.textContent = '+';

    slider.appendChild(minus);
    slider.appendChild(input);
    slider.appendChild(plus);

    input.addEventListener('input', function () {
      var wanted = input.value; // read first: a density re-check below rewrites the input
      checkRatio(s);
      setLevel(s, wanted);
      input.value = String(s.level);
    });
    minus.addEventListener('click', function (event) {
      if (pressedHere(s, minus, event)) nudge(s, -0.5);
    });
    plus.addEventListener('click', function (event) {
      if (pressedHere(s, plus, event)) nudge(s, 0.5);
    });

    s.slider = slider;
    s.input = input;
    return slider;
  }

  function buildClose(s) {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'image-magnify-close';
    button.setAttribute('aria-label', 'Close zoom');
    button.innerHTML =
      '<svg aria-hidden="true" focusable="false" viewBox="0 0 18 18">' +
      '<path d="M3.5 3.5 L14.5 14.5 M14.5 3.5 L3.5 14.5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" fill="none"/></svg>';
    button.addEventListener('click', function (event) {
      if (pressedHere(s, button, event)) end();
    });
    s.close = button;
    return button;
  }

  // Item 3: drag to move the picture.
  function press(s, event) {
    if (s.drag || !event.isPrimary) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    checkRatio(s);
    var box = s.overlay.getBoundingClientRect();
    var width = baseWidth(s, box) * s.level;
    var height = s.naturalWidth ? width * (s.naturalHeight / s.naturalWidth) : box.height * s.level;
    s.drag = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      fromX: s.x,
      fromY: s.y,
      spanX: width - box.width,
      spanY: height - box.height,
    };
    try {
      s.overlay.setPointerCapture(event.pointerId);
    } catch (error) {
      // the pointer is already gone; the drag ends on its own
    }
    s.overlay.setAttribute('data-dragging', 'true');
    document.documentElement.classList.add(DRAGGING_CLASS);
    if (event.pointerType === 'mouse') event.preventDefault(); // no text selection, no focus change
  }

  function move(s, event) {
    var d = s.drag;
    if (!d || event.pointerId !== d.id || s.level <= MIN_ZOOM) return;
    // background-position p% puts the photograph's p% point on the box's p% point, so the picture's left
    // edge sits at -span x p / 100; moving it by dx is a change of -dx / span x 100 in p.
    if (d.spanX > 0.5) s.x = clamp(d.fromX - ((event.clientX - d.x) / d.spanX) * 100, 0, 100);
    if (d.spanY > 0.5) s.y = clamp(d.fromY - ((event.clientY - d.y) / d.spanY) * 100, 0, 100);
    render(s);
  }

  function release(s, event) {
    var d = s.drag;
    if (!d || (event && event.pointerId !== d.id)) return;
    s.drag = null;
    document.documentElement.classList.remove(DRAGGING_CLASS);
    if (!s.overlay) return;
    s.overlay.setAttribute('data-dragging', 'false');
    try {
      if (s.overlay.hasPointerCapture(d.id)) s.overlay.releasePointerCapture(d.id);
    } catch (error) {
      // already released
    }
  }

  function show(s, src) {
    var overlay = document.createElement('div');
    overlay.className = 'image-magnify-full-size';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.setAttribute('data-dragging', 'false');
    overlay.style.backgroundImage = "url('" + src + "')";
    overlay.style.backgroundColor = 'var(--gradient-background)';
    overlay.style.backgroundRepeat = 'no-repeat';
    overlay.addEventListener('pointerdown', function (event) {
      press(s, event);
    });
    overlay.addEventListener('pointermove', function (event) {
      move(s, event);
    });
    overlay.addEventListener('pointerup', function (event) {
      release(s, event);
    });
    overlay.addEventListener('pointercancel', function (event) {
      release(s, event);
    });
    overlay.addEventListener('lostpointercapture', function (event) {
      release(s, event);
    });
    s.overlay = overlay;
    s.image.parentElement.insertBefore(overlay, s.image);
    s.max = zoomLimit(s);
    s.level = Math.min(s.level, s.max);
    s.box.appendChild(buildClose(s));
    s.box.appendChild(buildSlider(s));
    setLevel(s, s.level);
    placeControls(s);
  }

  function stopLoading(s) {
    if (!s.loading) return;
    s.loading = false;
    if (s.spinner) s.spinner.classList.add('hidden');
    s.image.style.opacity = s.previousOpacity;
  }

  function isShown(image) {
    return image.isConnected && image.getClientRects().length > 0;
  }

  // Another picture chosen by any means (mouse, keyboard, a variant change) takes "is-active" off this
  // photograph's slide, and in the thumbnail layouts hides it. The zoom must not stay open on it.
  function watchSlide(s) {
    var item = s.image.closest('.product__media-item');
    if (!item || typeof MutationObserver !== 'function') return;
    var wasActive = item.classList.contains('is-active');
    s.observer = new MutationObserver(function () {
      if (session !== s) return;
      if ((wasActive && !item.classList.contains('is-active')) || !isShown(s.image)) end();
    });
    s.observer.observe(item, { attributes: true, attributeFilter: ['class'] });
  }

  function start(image, event) {
    if (session && session.image === image) return; // already open, or still loading, on this photograph
    if (session) end();
    if (isNarrow()) return; // below 750 px the photograph belongs to Dawn's full-screen viewer

    var s = {
      image: image,
      box: image.closest('.product__modal-opener') || image.parentElement,
      level: DEFAULT_ZOOM,
      max: MAX_ZOOM,
      limitExact: null,
      ratio: window.devicePixelRatio || 1,
      x: 50,
      y: 50,
      naturalWidth: 0,
      naturalHeight: 0,
      overlay: null,
      slider: null,
      input: null,
      close: null,
      observer: null,
      drag: null,
      pressed: null,
      loading: true,
      spinner: spinnerOf(image),
      previousOpacity: image.style.opacity,
    };
    session = s;
    pointAt(s, event);
    watchSlide(s);

    // As Dawn does: dim the photograph and show the spinner until the large copy has arrived.
    var src = image.src;
    image.style.opacity = '50%';
    if (s.spinner) s.spinner.classList.remove('hidden');
    var loader = document.createElement('img');
    loader.onload = function () {
      if (session !== s) return; // ended while it was loading
      s.naturalWidth = loader.naturalWidth;
      s.naturalHeight = loader.naturalHeight;
      stopLoading(s);
      show(s, src);
    };
    loader.onerror = function () {
      if (session === s) end();
    };
    loader.setAttribute('src', src);
  }

  function end() {
    var s = session;
    if (!s) return;
    session = null;
    if (s.observer) s.observer.disconnect();
    s.drag = null;
    document.documentElement.classList.remove(DRAGGING_CLASS);
    stopLoading(s);
    if (s.overlay) s.overlay.remove();
    if (s.close) s.close.remove();
    if (s.slider) s.slider.remove();
  }

  function install() {
    injectStyle();
    window[VERSION_FLAG] = VERSION;
    // The theme editor runs this file again after a section reloads; everything below is delegated to
    // the document, so it is installed once and serves photographs added later as well.
    if (window[INSTALLED_FLAG]) return;
    window[INSTALLED_FLAG] = true;

    document.addEventListener('click', function (event) {
      var target = event.target;
      if (!(target instanceof Element)) return;
      var image = target.closest('img.image-magnify-hover');
      if (image) start(image, event);
    });

    // A touch or pen that starts outside the photograph: remembered, and it ends the zoom only if it
    // turns out to be a tap. A swipe (it travels, or the browser takes it over to scroll) keeps the zoom.
    var tap = null;

    function travelled(event) {
      return Math.abs(event.clientX - tap.x) + Math.abs(event.clientY - tap.y) > TAP_SLOP_PX;
    }

    document.addEventListener(
      'pointerdown',
      function (event) {
        tap = null;
        if (!session) return;
        // What the press began on: the close button and the - and + act only if the release is on them too.
        session.pressed = event.target instanceof Element ? event.target : null;
        if (event.target instanceof Node && session.box.contains(event.target)) return;
        if (event.pointerType === 'touch' || event.pointerType === 'pen') {
          tap = { id: event.pointerId, x: event.clientX, y: event.clientY };
          return;
        }
        end();
      },
      true
    );

    document.addEventListener(
      'pointermove',
      function (event) {
        if (tap && event.pointerId === tap.id && travelled(event)) tap = null;
      },
      true
    );

    document.addEventListener('keydown', function (event) {
      if (session && (event.key === 'Escape' || event.key === 'Esc')) end();
    });

    window.addEventListener(
      'pointerup',
      function (event) {
        if (!tap || event.pointerId !== tap.id) return;
        var wasTap = !travelled(event);
        tap = null;
        if (wasTap) end();
      },
      true
    );
    window.addEventListener(
      'pointercancel',
      function () {
        tap = null;
      },
      true
    );

    function onNarrow() {
      if (isNarrow()) end();
    }
    if (narrow) {
      if (narrow.addEventListener) narrow.addEventListener('change', onNarrow);
      else if (narrow.addListener) narrow.addListener(onNarrow);
    }

    var queued = false;
    function refresh() {
      if (queued || !session) return;
      queued = true;
      window.requestAnimationFrame(function () {
        queued = false;
        if (!session) return;
        if (isNarrow() || !isShown(session.image)) {
          end();
          return;
        }
        if (session.overlay) applyLimit(session);
        render(session);
        placeControls(session);
      });
    }
    window.addEventListener('resize', refresh, { passive: true });
    window.addEventListener('scroll', refresh, { passive: true });

    // The pixel density changes without a resize when the window moves to another screen; a query for the
    // present density stops matching then. It is set up again for the new density each time.
    var ratioQuery = null;
    function onRatio() {
      watchRatio();
      refresh();
    }
    function watchRatio() {
      if (!window.matchMedia) return;
      if (ratioQuery) {
        if (ratioQuery.removeEventListener) ratioQuery.removeEventListener('change', onRatio);
        else if (ratioQuery.removeListener) ratioQuery.removeListener(onRatio);
      }
      ratioQuery = window.matchMedia('(resolution: ' + (window.devicePixelRatio || 1) + 'dppx)');
      if (ratioQuery.addEventListener) ratioQuery.addEventListener('change', onRatio);
      else if (ratioQuery.addListener) ratioQuery.addListener(onRatio);
    }
    watchRatio();
  }

  install();
})();
